import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import ContentRichTextEditor from '../../components/admin/ContentRichTextEditor';
import { memberEducationToHtml } from '../../utils/richText';
import {
  MEMBER_GROUPS,
  memberGroupLabel,
  normalizeMemberGroup,
  type MemberGroupKey,
} from '../../constants/memberGroups';
import MediaImage from '../../components/MediaImage';

type Member = {
  _id?: string;
  name: string;
  title?: string;
  group?: string;
  email?: string;
  location?: string;
  homepage?: string;
  avatarUrl?: string;
  researchDirections?: string[];
  education?: string;
  bio?: string;
  order?: number;
};

const GROUP_ORDER_BASE: Partial<Record<MemberGroupKey, number>> = {
  PI: 1,
  Postdoc: 2,
  PhD: 10,
  Master: 20,
  Undergraduate: 25,
  Graduated: 30,
  MasterBsGraduated: 35,
  Other: 40,
};

const sortMembersForAdmin = (list: Member[]): Member[] =>
  [...list].sort((a, b) => {
    const ga = MEMBER_GROUPS.findIndex(
      (g) => g.key === normalizeMemberGroup(a.group)
    );
    const gb = MEMBER_GROUPS.findIndex(
      (g) => g.key === normalizeMemberGroup(b.group)
    );
    if (ga !== gb) return ga - gb;
    const oa = a.order ?? 0;
    const ob = b.order ?? 0;
    if (oa !== ob) return oa - ob;
    return a.name.localeCompare(b.name);
  });

/** Order for a new member — smallest in group minus 1 so they appear first. */
const firstOrderInGroup = (list: Member[], group: MemberGroupKey): number => {
  const inGroup = list.filter(
    (m) => normalizeMemberGroup(m.group) === group
  );
  if (inGroup.length === 0) return GROUP_ORDER_BASE[group] ?? 0;
  return Math.min(...inGroup.map((m) => m.order ?? 0)) - 1;
};

const emptyMember: Member = {
  name: '',
  title: '',
  group: '',
  email: '',
  location: '',
  homepage: '',
  researchDirections: [],
  education: '',
  bio: '',
};

const AdminMembers: React.FC = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Member | null>(null);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [activeGroup, setActiveGroup] = useState<MemberGroupKey | 'all'>('all');
  const [error, setError] = useState<string | null>(null);

  const sortedMembers = useMemo(() => sortMembersForAdmin(members), [members]);

  const visibleMembers = useMemo(() => {
    if (activeGroup === 'all') return sortedMembers;
    return sortedMembers.filter(
      (m) => normalizeMemberGroup(m.group) === activeGroup
    );
  }, [sortedMembers, activeGroup]);

  const loadMembers = async () => {
    try {
      setLoading(true);
      const res = await api.get<Member[]>('/members');
      setMembers(res.data);
    } catch (err) {
      console.error(err);
      setError('Failed to load members.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
  }, []);

  const openCreate = () => {
    setEditing({ ...emptyMember });
    setError(null);
  };

  const openEdit = (m: Member) => {
    setEditing({
      ...m,
      researchDirections: m.researchDirections || [],
      education: memberEducationToHtml(m.education),
      bio: m.bio || '',
    });
    setError(null);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (!editing) return;
    const { name, value } = e.target;
    setEditing({ ...editing, [name]: value });
  };

  const handleDirectionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editing) return;
    const value = e.target.value;
    const arr = value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    setEditing({ ...editing, researchDirections: arr });
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.name) {
      setError('Please enter a name.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const payload = { ...editing };
      if (!payload._id) {
        const group = normalizeMemberGroup(payload.group);
        payload.order = firstOrderInGroup(members, group);
      }
      if (payload._id) {
        await api.put(`/members/${payload._id}`, payload);
      } else {
        await api.post('/members', payload);
      }
      setEditing(null);
      await loadMembers();
    } catch (err) {
      console.error(err);
      setError('Save failed. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (m: Member) => {
    if (!m._id) return;
    const ok = window.confirm(
      `Are you sure you want to delete "${m.name}"? This cannot be undone.`
    );
    if (!ok) return;
    try {
      setError(null);
      await api.delete(`/members/${m._id}`);
      if (editing?._id === m._id) setEditing(null);
      await loadMembers();
    } catch (err) {
      console.error(err);
      setError('Delete failed. Please try again later.');
    }
  };

  const moveMember = async (index: number, direction: 'up' | 'down') => {
    const list = visibleMembers;
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= list.length) return;

    const current = list[index];
    const sibling = list[swapIndex];
    if (
      normalizeMemberGroup(current.group) !== normalizeMemberGroup(sibling.group)
    ) {
      return;
    }

    const groupKey = normalizeMemberGroup(current.group);
    const groupMembers = sortedMembers.filter(
      (m) => normalizeMemberGroup(m.group) === groupKey
    );
    const fromIdx = groupMembers.findIndex((m) => m._id === current._id);
    const toIdx = groupMembers.findIndex((m) => m._id === sibling._id);
    const reordered = [...groupMembers];
    [reordered[fromIdx], reordered[toIdx]] = [reordered[toIdx], reordered[fromIdx]];
    const base = GROUP_ORDER_BASE[groupKey] ?? 0;

    try {
      setReordering(true);
      setError(null);
      await Promise.all(
        reordered.map((m, i) =>
          api.put(`/members/${m._id}`, { order: base + i })
        )
      );
      await loadMembers();
    } catch (err) {
      console.error(err);
      setError('Failed to update display order. Please try again.');
    } finally {
      setReordering(false);
    }
  };

  const handleAvatarUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!editing) return;
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post<{ url?: string; path?: string }>(
        '/uploads/avatar',
        formData,
        { timeout: 60000 }
      );
      const rawPath = res.data.path || '';
      const base = (api.defaults.baseURL as string | undefined) || '';
      const origin = base.startsWith('http')
        ? new URL(base).origin
        : window.location.origin;
      const stored =
        rawPath ||
        (res.data.url?.includes('/api/uploads/')
          ? new URL(res.data.url).pathname
          : res.data.url || '');
      setEditing((prev) => (prev ? { ...prev, avatarUrl: stored } : prev));
    } catch (err) {
      console.error(err);
      // eslint-disable-next-line no-alert
      alert('AvatarUpload failed. Please try again later.');
    } finally {
      e.target.value = '';
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-serif text-2xl font-semibold text-neutral-900">
          Member Management
        </h1>
        <button
          onClick={openCreate}
          className="rounded-full bg-brand-red px-4 py-1.5 text-xs font-semibold tracking-[0.18em] text-white hover:bg-red-700"
        >
          Add Member
        </button>
      </div>
      <p className="mb-3 text-xs text-neutral-500">
        Use Move up / Move down within the same group to control the order on the
        public Members page. Smaller order values appear first.
      </p>
      {loading ? (
        <p className="text-sm text-neutral-600">Loading...</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white shadow-subtle">
          <div className="flex flex-wrap items-center gap-2 border-b border-neutral-200 bg-neutral-50 px-3 py-2 text-[11px] text-neutral-700">
            <span className="mr-1">Group:</span>
            <button
              type="button"
              onClick={() => setActiveGroup('all')}
              className={`rounded-full px-3 py-0.5 ${
                activeGroup === 'all'
                  ? 'bg-brand-red text-white'
                  : 'bg-white text-neutral-700 hover:bg-neutral-100'
              }`}
            >
              All
            </button>
            {MEMBER_GROUPS.map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => setActiveGroup(g.key)}
                className={`rounded-full px-3 py-0.5 ${
                  activeGroup === g.key
                    ? 'bg-brand-red text-white'
                    : 'bg-white text-neutral-700 hover:bg-neutral-100'
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
          <table className="min-w-full text-left text-xs">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Order
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Name
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Title/Role
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Group
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Email
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Research Directions
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleMembers.map((m, index) => (
                <tr key={m._id} className="border-b border-neutral-100">
                  <td className="px-3 py-2 text-neutral-700">
                    <div className="flex items-center gap-1">
                      <span className="min-w-[1.5rem]">{m.order ?? 0}</span>
                      <button
                        type="button"
                        onClick={() => moveMember(index, 'up')}
                        disabled={index === 0 || reordering}
                        className="rounded px-1 py-0.5 text-[10px] text-neutral-600 hover:bg-neutral-200 disabled:opacity-40"
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveMember(index, 'down')}
                        disabled={
                          index === visibleMembers.length - 1 || reordering
                        }
                        className="rounded px-1 py-0.5 text-[10px] text-neutral-600 hover:bg-neutral-200 disabled:opacity-40"
                        title="Move down"
                      >
                        ↓
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-neutral-900">{m.name}</td>
                  <td className="px-3 py-2 text-neutral-700">{m.title}</td>
                  <td className="px-3 py-2 text-neutral-700">{memberGroupLabel(m.group)}</td>
                  <td className="px-3 py-2 text-neutral-700">{m.email}</td>
                  <td className="px-3 py-2 text-neutral-700">
                    {m.researchDirections?.join('，')}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button
                      onClick={() => openEdit(m)}
                      className="mr-2 text-xs text-brand-red hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(m)}
                      className="text-xs text-neutral-500 hover:text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {visibleMembers.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-4 text-center text-xs text-neutral-500"
                  >
                    No members yet. Click Add Member in the top-right corner.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Basic edit modal */}
      {editing && (
        <div className="fixed inset-0 z-40 flex items-center justify-center overflow-y-auto bg-black/30 p-4">
          <div className="flex max-h-[min(94vh,900px)] w-full max-w-4xl flex-col rounded-lg bg-white shadow-subtle">
            <div className="shrink-0 border-b border-neutral-200 px-6 py-4">
              <h2 className="text-sm font-semibold text-neutral-900">
                {editing._id ? 'Edit Member' : 'Add Member'}
              </h2>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            <div className="space-y-3 text-xs">
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-neutral-100 text-sm font-semibold text-neutral-500">
                  {editing.avatarUrl ? (
                    <MediaImage
                      src={editing.avatarUrl}
                      alt={editing.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    editing.name.charAt(0) || 'Avatar'
                  )}
                </div>
                <label className="inline-flex cursor-pointer items-center rounded-full border border-neutral-300 px-3 py-1 text-[11px] font-medium text-neutral-700 hover:bg-neutral-50">
                  Upload avatar
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                  />
                </label>
              </div>
              <div>
                <label className="mb-1 block font-medium text-neutral-700">
                  Name
                </label>
                <input
                  name="name"
                  value={editing.name}
                  onChange={handleChange}
                  className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                />
              </div>
              <div>
                <label className="mb-1 block font-medium text-neutral-700">
                  Title/Role
                </label>
                <input
                  name="title"
                  value={editing.title || ''}
                  onChange={handleChange}
                  className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                />
              </div>
              <div>
                <label className="mb-1 block font-medium text-neutral-700">
                  Group
                </label>
                <select
                  name="group"
                  value={editing.group || ''}
                  onChange={handleChange}
                  className="um-input w-full bg-white px-2 py-1.5"
                >
                  <option value="">Please select</option>
                  {MEMBER_GROUPS.map((g) => (
                    <option key={g.key} value={g.key}>
                      {g.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block font-medium text-neutral-700">
                  Email
                </label>
                <input
                  name="email"
                  type="email"
                  value={editing.email || ''}
                  onChange={handleChange}
                  className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                />
              </div>
              <div>
                <label className="mb-1 block font-medium text-neutral-700">
                  Location
                </label>
                <input
                  name="location"
                  value={editing.location || ''}
                  onChange={handleChange}
                  placeholder="University of Macau · Research Building"
                  className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                />
              </div>
              <div>
                <label className="mb-1 block font-medium text-neutral-700">
                  Personal homepage
                </label>
                <input
                  name="homepage"
                  type="url"
                  value={editing.homepage || ''}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                />
              </div>
              <div>
                <label className="mb-1 block font-medium text-neutral-700">
                  Research directions (comma separated)
                </label>
                <input
                  value={(editing.researchDirections || []).join(', ')}
                  onChange={handleDirectionsChange}
                  className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                />
              </div>
              <ContentRichTextEditor
                label="Research bio (optional)"
                value={editing.bio || ''}
                onChange={(html) =>
                  setEditing((prev) => (prev ? { ...prev, bio: html } : prev))
                }
                helperText="Supports subscript/superscript, lists, links, and images."
              />
              <ContentRichTextEditor
                label="Education"
                value={editing.education || ''}
                onChange={(html) =>
                  setEditing((prev) => (prev ? { ...prev, education: html } : prev))
                }
                helperText="One entry per paragraph. Supports subscript/superscript (e.g. CO₂)."
              />
            </div>
            {error && (
              <p className="mt-2 text-[11px] text-red-600">
                {error}
              </p>
            )}
            </div>
            <div className="shrink-0 border-t border-neutral-200 px-6 py-4">
              <div className="flex justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                className="rounded-full border border-neutral-300 px-4 py-1 text-xs text-neutral-700 hover:bg-neutral-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="rounded-full bg-brand-red px-4 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMembers;

